import { Controller } from 'stimulus';

// Walkthrough modal: owns the celebration burst, the header video's play button and its analytics.
export default class extends Controller {
  static targets = ['canvas', 'video', 'playButton'];

  static values = {
    projectId: Number,
    designType: String,
    designId: Number,
    video: Boolean,
  };

  #shown = false;
  #videoPlayed = false;

  async connect() {
    this.syncPlayButton();
    // The parent modal relocates itself into #modals, which reconnects this controller.
    if (!this.#shown) {
      this.#shown = true;
      this.#track('First Design Modal Viewed');
    }

    if (!this.hasCanvasTarget) {
      return;
    }
    // Split out of the main bundle: only this modal needs canvas-confetti.
    const { default: confetti } = await import('canvas-confetti');
    // The modal can already be gone by the time the chunk lands.
    if (!this.canvasTarget.isConnected) {
      return;
    }

    // No useWorker: it leaves the canvas at its 1x1 backing size and nothing renders.
    this.confetti = confetti.create(this.canvasTarget, { resize: true });
    // Velocity and gravity are canvas pixels, so these track the canvas size above.
    this.confetti({
      particleCount: 320,
      spread: 110,
      startVelocity: 48,
      // Low gravity plus high ticks so particles reach the bottom before expiring.
      gravity: 0.4,
      ticks: 330,
      origin: { y: 0.7 },
      disableForReducedMotion: true,
    });
  }

  // disconnect() also covers Turbo navigation, which the modal's after-close event misses.
  disconnect() {
    this.confetti?.reset();
  }

  // The modal isn't dismissible, so Continue is the only way out; board.js starts the walkthrough.
  startWalkthrough() {
    this.#track('First Design Modal Walkthrough Started');
  }

  // Only reachable behind :first_design_modal_v2, the only case where the video renders.
  trackVideoPlayed() {
    if (this.#videoPlayed) {
      return;
    }
    this.#videoPlayed = true;
    this.#track('First Design Modal Video Watched');
  }

  play() {
    // A rejected play() leaves the button in place on its own; just silence the rejection.
    const started = this.videoTarget.play();
    if (started) {
      started.catch(() => {});
    }
  }

  // Hidden while the video plays, back on pause/end, so it tracks the native controls too.
  syncPlayButton() {
    if (this.hasVideoTarget && this.hasPlayButtonTarget) {
      this.playButtonTarget.classList.toggle('hidden', !this.videoTarget.paused);
    }
  }

  #track(event, properties = {}) {
    window.mixpanel?.track(event, {
      project_id: this.projectIdValue,
      design_type: this.designTypeValue,
      // board_id or fp_view_id
      [`${this.designTypeValue}_id`]: this.designIdValue,
      video: this.videoValue,
      ...properties,
    });
  }
}
